import { Card, CardContent } from '@/components/ui/card';
import { BarChart3, TrendingUp, FileText, Calendar, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface NewsReportsSectionEnProps {
  handleViewReports: () => void;
}

const NewsReportsSectionEn = ({ handleViewReports }: NewsReportsSectionEnProps) => {
  return (
  <section className="py-24 bg-white">
    <div className="w-full px-8 md:px-16 lg:px-24">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16 animate-on-scroll">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6 text-gray-900">Latest News & Reports</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Stay updated with the latest developments in Sweden's startup ecosystem
          </p>
        </div>
        <div className="grid grid-cols-9 gap-8">
          {/* First row: 3 cards, each col-span-3 */}
          <div className="col-span-3 animate-on-scroll">
              <Card className="border-0 shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden h-full bg-[hsl(var(--brand-white))]">
              <div className="aspect-[4/3] relative">
                <img
                  src="/lovable-uploads/80defe76-9423-4628-bf6e-02915adc3612.png"
                  alt="Sweden Startup Ecosystem Report 2025"
                  className="w-full h-full object-cover absolute inset-0"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-blue-500/70 to-purple-600/70"></div>
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 bg-emerald-500 text-white text-xs font-medium rounded-full inline-flex items-center">
                    <BarChart3 className="w-3 h-3 mr-1" />
                    REPORT
                  </span>
                </div>
              </div>
              <CardContent className="p-6 bg-white">
                <div className="flex items-center text-sm text-gray-500 mb-2">
                  <Calendar className="w-3 h-3 mr-1" />
                  Nov 15, 2025
                </div>
                <h3 className="text-xl font-bold mb-2 text-gray-900">Sweden Startup Ecosystem Report 2025</h3>
                <p className="text-gray-600 text-sm">Comprehensive analysis of the Swedish startup landscape including funding trends, sector analysis, and policy recommendations.</p>
              </CardContent>
            </Card>
          </div>
          <div className="col-span-3 animate-on-scroll">
              <Card className="border-0 shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden h-full bg-[hsl(var(--brand-white))]">
              <div className="aspect-[4/3] relative">
                <img
                  src="/lovable-uploads/ff6b06fe-1fc6-45c1-8996-653584c7e69e.png"
                  alt="Why Sweden lost MARLEQ"
                  className="w-full h-full object-cover absolute inset-0"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-purple-500/70 to-pink-500/70"></div>
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 bg-violet-500 text-white text-xs font-medium rounded-full inline-flex items-center">
                    <TrendingUp className="w-3 h-3 mr-1" />
                    INSIGHTS
                  </span>
                </div>
              </div>
              <CardContent className="p-6 bg-white">
                <div className="flex items-center text-sm text-gray-500 mb-2">
                  <Calendar className="w-3 h-3 mr-1" />
                  Jun 18, 2025
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-900">Why Sweden lost MARLEQ</h3>
                <p className="text-gray-600 text-sm">Insights from global entrepreneur Milo Radulovic on systemic failures and improvements needed for Swedish startups.</p>
              </CardContent>
            </Card>
          </div>
          <div className="col-span-3 animate-on-scroll">
              <Card className="border-0 shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden h-full bg-[hsl(var(--brand-white))]">
              <div className="aspect-[4/3] relative">
                <img
                  src="/lovable-uploads/80defe76-9423-4628-bf6e-02915adc3612.png"
                  alt="Record Breaking Q1 2025 Funding"
                  className="w-full h-full object-cover absolute inset-0"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-green-500/70 to-blue-500/70"></div>
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 bg-orange-500 text-white text-xs font-medium rounded-full inline-flex items-center">
                    <FileText className="w-3 h-3 mr-1" />
                    NEWS
                  </span>
                </div>
              </div>
              <CardContent className="p-6 bg-white">
                <div className="flex items-center text-sm text-gray-500 mb-2">
                  <Calendar className="w-3 h-3 mr-1" />
                  Mar 8, 2025
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-900">Record Breaking Q1 2025 Funding</h3>
                <p className="text-gray-600 text-sm">Swedish startups raised €2.1 billion in Q1, marking a 47% increase from the previous year.</p>
              </CardContent>
            </Card>
          </div>
          {/* Second row: 1 card (col-span-3), 1 double box (col-span-6) */}
          <div className="col-span-3 animate-on-scroll mt-8">
            <Card className="border-0 shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden h-full bg-[hsl(var(--brand-white))]">
              <div className="aspect-[4/3] relative">
                <img
                  src="/lovable-uploads/ff6b06fe-1fc6-45c1-8996-653584c7e69e.png"
                  alt="Sweden has the research"
                  className="w-full h-full object-cover absolute inset-0"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/70 to-purple-500/70"></div>
                <div className="absolute inset-0 bg-black/20"></div>
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 bg-violet-500 text-white text-xs font-medium rounded-full inline-flex items-center">
                    <TrendingUp className="w-3 h-3 mr-1" />
                    INSIGHTS
                  </span>
                </div>
              </div>
              <CardContent className="p-6 bg-white">
                <div className="flex items-center text-sm text-gray-500 mb-2">
                  <Calendar className="w-3 h-3 mr-1" />
                  Jun 11, 2025
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-900">Sweden has the research</h3>
                <p className="text-gray-600 text-sm">Analysis of why Sweden lacks customers and technological optimism despite strong research capabilities.</p>
              </CardContent>
            </Card>
          </div>
          <div className="col-span-6 animate-on-scroll mt-8 flex items-center justify-center">
            <div className="w-full h-full bg-white rounded-2xl p-10 flex flex-col items-center justify-center shadow-xl">
              <h3 className="text-2xl lg:text-3xl font-bold mb-4 text-gray-900">Why we work with reports and policy</h3>
              <p className="text-lg text-gray-700 max-w-2xl text-center">
                Reports and policy work are essential to drive evidence-based change in the Swedish startup ecosystem. By analyzing trends, sharing insights, and influencing policy, we help create better conditions for innovation, growth, and global competitiveness.
              </p>
            </div>
          </div>
        </div>
        <div className="text-center mt-12 animate-on-scroll">
          <Button variant="cyan" className="px-8 py-4 text-lg font-semibold rounded-full" onClick={handleViewReports}>
            View All News & Reports
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </div>
    </div>
  </section>
  );
};

export default NewsReportsSectionEn;
